import { useEffect, useCallback } from "react";
import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { RfxLeadViewToggleBanner } from "newui/features/Rfx/components/RfxLeadViewToggleBanner";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { RFX_TABS } from "utils/constants";
import { useEvaluation } from "../hooks/useEvaluation";
import { useEvaluationRfxId } from "../hooks/useEvaluationRfxId";
import { InitialEvaluationProvider } from "../hooks/InitialEvaluationContext";
import { RFxEvaluationSteps } from "../components/RFxEvaluationSteps";
import { EvaluationAccessBoundary } from "../components/EvaluationAccessBoundary";
import { RfxLeadInfoBox, InvitationBanner } from "../components/steps/InitialEvaluationStep/panel";
import { StepperLegend } from "newui/components/Stepper";
import type { PanelMode } from "../types/panel.type";
import { isSoloPanelMode } from "../utils/panelModes";
import { useIsReadOnlyMode } from "newui/features/ExecutiveView";
import { useCurrentCompanyId } from "hooks/auth";

interface EvaluationPageProps {
	evaluationId?: string;
	path?: string;
}

export const EvaluationPage: React.FC<EvaluationPageProps> = () => {
	const { rfxData, viewAsRfxLead } = useRfxData();
	// TASK-028: see `useEvaluationRfxId` — never derive this from `rfxData?.id`.
	const rfxId = useEvaluationRfxId(rfxData);
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();
	const isReadOnly = useIsReadOnlyMode();
	const currentCompanyId = useCurrentCompanyId();

	const { isLoading, evaluationState, currentUserContext, accessError, fetchEvaluationState, updateProgress, advanceStep } = useEvaluation({
		rfxId,
	});

	// Visibility-only lead flag. A switched executive (read-only) can opt into
	// "View as RFx Lead" to reveal the lead-gated management UI for VIEWING. This
	// never enables writes: the real `isRfxLead` still drives all write/readOnly
	// gating inside the steps, and the pointer-events-none wrapper below
	// neutralises every interaction while read-only.
	const effectiveIsRfxLead =
		(currentUserContext?.isRfxLead ?? false) || (isReadOnly && viewAsRfxLead);

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleBack = (): void => {
		navigate(clientPaths.viewRfx(rfxData, RFX_TABS.ALL_INVITED_SUPPLIER));
	};

	// Step changes are UI-only navigation — the backend owns step progression
	// via the advance-step endpoint, called by each step's onBeforeNext.
	const handleStepChange = useCallback(
		(_step: number) => {
			// No-op: backend step is advanced via advanceStep(), not here.
		},
		[]
	);

	const handlePanelModeChange = useCallback(
		(mode: PanelMode) => {
			updateProgress({ panel_mode: mode });
		},
		[updateProgress]
	);

	if (isLoading) {
		return (
			<div className="space-y-3 p-6">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	/**
	 * Deliberately NOT passed `scoringBlockedReason`. It blocks every child,
	 * including the `InvitationBanner` below — so a member who declined, or
	 * whose declared conflict is awaiting review, hit a dead-end "Access
	 * Denied" wall with no way to re-accept or withdraw. This boundary is for
	 * genuine 403s (not a panel member, cross-company, revoked); "declined"
	 * and "conflict_pending_review" are in-flow panel states, not access
	 * denials, and the member must still reach the banner.
	 *
	 * The scoring surface itself stays gated: `scoringBlockedReason` is
	 * threaded to `PanelMemberScoringView` via `RFxEvaluationSteps`, which
	 * already renders the conflict-pending state instead of the score form.
	 * `EvaluationTabDisplay` wraps this same boundary the same 403-only way.
	 */
	return (
		<EvaluationAccessBoundary rfxLead={rfxData?.rfxLead} error={accessError}>
			<InitialEvaluationProvider rfxId={rfxId}>
				<RfxLeadViewToggleBanner />
				<div data-testid="evaluation-page" className="p-6">
					<div className="mb-4 flex flex-row items-center">
						{currentCompanyId === rfxData?.companyId && <Button btntype="secondary" onClick={handleBack}>
							← Back to RFx
						</Button>}
						<div className="ml-4 flex flex-col">
							<div>
								RFx: {rfxData?.name}
							</div>
							{rfxData?.project && <div>
								Project: {rfxData?.project?.name}
							</div>}
						</div>
						<StepperLegend className="ml-auto flex flex-wrap items-center justify-end gap-4 border border-gray-200 p-4 rounded" />
					</div>
					<div className="mb-6 flex items-start justify-between">
						<div>
							<h1 className="text-2xl font-semibold text-gray-900">
								{hasAIAccess ? (
									<>EvaluA<span className="text-utility-brand-700">I</span>tion tool</>
								) : (
									"Evaluation"
								)}
							</h1>
							<p className="mt-1 text-sm text-gray-500">
								{hasAIAccess
									? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
									: "Systematically score and compare supplier submissions."}
							</p>
						</div>
						<div className="flex items-center gap-3 shrink-0">
							{!currentUserContext?.isRfxLead && rfxData?.rfxLead && (
								<RfxLeadInfoBox rfxLead={rfxData.rfxLead} />
							)}
							{currentUserContext?.isRfxLead && (
								<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-lg font-medium bg-purple-100 text-purple-700">
									RFx Lead
								</span>
							)}
							{!isSoloPanelMode(evaluationState?.panelMode) && (
								currentUserContext?.panelMemberId != null ? (
									<span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-lg font-medium text-blue-700">
										Panel Member
									</span>
								) : (
									<span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-lg font-medium text-amber-700">
										Non-Panel Member
									</span>
								)
							)}
						</div>
					</div>
					{isReadOnly && (
						<div
							className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
							data-testid="evaluation-readonly-notice"
						>
							<span className="font-medium">View only.</span>{" "}
							Scoring and submission are disabled while viewing as another company.
						</div>
					)}
					{!effectiveIsRfxLead && currentUserContext?.panelMemberStatus && (
						<div className="mb-4">
							<InvitationBanner
								evalId={evaluationState?.id}
								memberId={currentUserContext?.panelMemberId}
								rfxData={rfxData}
								rfxId={rfxId}
								currentStatus={currentUserContext?.panelMemberStatus}
								currentStep={evaluationState?.currentStep}
								onAccept={fetchEvaluationState}
								onDecline={fetchEvaluationState}
								disclaimerRequired={currentUserContext?.disclaimerRequired}
								isConflictPending={
									currentUserContext?.scoringBlockedReason === "conflict_pending_review"
								}
							/>
						</div>
					)}
					<div
						className={isReadOnly ? "pointer-events-none select-none opacity-75" : undefined}
						data-testid={isReadOnly ? "evaluation-steps-readonly" : "evaluation-steps"}
					>
						<RFxEvaluationSteps
							rfxData={rfxData}
							evaluationState={evaluationState}
							isRfxLead={currentUserContext?.isRfxLead ?? false}
							isRfxLeadView={effectiveIsRfxLead}
							onComplete={handleBack}
							onStepChange={handleStepChange}
							onPanelModeChange={handlePanelModeChange}
							onAdvanceStep={advanceStep}
							onRefreshUserContext={fetchEvaluationState}
						/>
					</div>
				</div>
			</InitialEvaluationProvider>
		</EvaluationAccessBoundary>
	);
};
